"use client";

/**
 * Haptics shim — abstracts the underlying vibration / haptic-impact
 * primitive behind a tiny three-tier API (`light`, `medium`, `heavy`).
 *
 * ## Why this exists
 *
 * Set-logging on the active workout card fires haptic feedback on
 * every keypad press, every field-advance, and the final commit
 * (lifters notice + appreciate it — it sells the "this just
 * happened" moment). The active-workout numeric keypad is the first
 * caller; future surfaces (rest-cycle tick, PR celebration, etc.)
 * can adopt the same three tiers without re-deciding the underlying
 * primitive each time.
 *
 * ## Today: `navigator.vibrate`
 *
 * Web implementation pings `navigator.vibrate(ms)` with millisecond
 * durations tuned for each tier.
 *
 * ## WHAT WE ACTUALLY KNOW, which is less than this file used to claim
 *
 * An earlier version of this docblock said "iOS Safari 17.4+ forwards short
 * pulses through CoreHaptics for a tactile-feeling tap". That sentence was
 * uncited, entered at this file's birth, was never revisited, and went on to
 * be cited BY other files as though it were established - so the repo ended
 * up agreeing with itself three times about something nobody had tested.
 *
 * Replaced with what is recorded rather than what is assumed:
 *
 *   - Android: observed working (see circuit-tone.ts, which notes the buzz
 *     as the Android fallback for its audio cue).
 *   - iOS: `navigator.vibrate` IS NOT DEFINED. Feature-detected on an
 *     iPhone 14 Pro Max, installed PWA, 2026-09-20 - not inferred from a
 *     buzz nobody felt, read off the object. So `pulse` below short-circuits
 *     on its `typeof` guard every single time, and all ~57 haptics calls in
 *     this app are no-ops on that platform.
 *
 * ## The other route, measured, because it was worth six tests
 *
 * iOS 17.4 added a native switch control to the web
 * (`<input type="checkbox" switch>`), and toggling one produces a system
 * haptic. That is real - the attribute is supported and a finger on a switch
 * buzzes. It is also useless to us, for a reason worth writing down so nobody
 * re-runs the experiment:
 *
 *   test                                        result
 *   your finger on a real switch                FELT IT
 *   a button handler calling switch.click()     nothing
 *   a button handler setting switch.checked     nothing
 *   the same, fired 3s later with no gesture    nothing
 *   the same, .checked, 3s later                nothing
 *
 * The haptic comes from the native control's own TOUCH handling, not from
 * the state change - so it cannot be synthesised from script at all. Not on
 * a timer, and not even inside a gesture we are already handling.
 *
 * Which leaves exactly one way to have it: make the element the finger
 * actually lands on BE a switch. Every digit key would become a checkbox
 * wearing a button's clothes, announced to a screen reader as "switch, off",
 * on the most-used control in the app. That is a real accessibility
 * regression traded for a subtle buzz, and it is not worth it.
 *
 * ## So
 *
 * There is no haptic on iOS from the web. Not by this API, not by that
 * trick, not on a timer. A native wrapper (Capacitor Haptics, Taptic Engine)
 * is the only route, and the three tiers below are already the right shape
 * for it - the call sites do not move.
 *
 * Until then: never build a feature whose only feedback is a buzz.
 *
 * Tier durations:
 *   - `light()`   → 10 ms — digit press, backspace, clear `×`. Subtle,
 *                  meant to register the input but not draw attention.
 *   - `medium()`  → 20 ms — field-advance (Done on a non-final field),
 *                  wheel-snap when the wheel picker lands in Phase A.5.
 *                  Confirms "you moved on to the next thing."
 *   - `heavy()`   → 30 ms — Done on the final field (set commit). Gives
 *                  the action a "this just happened" finality that
 *                  lifters notice — they can tell the difference between
 *                  field-advance and commit by feel alone.
 *
 * ## Future: `@capacitor/haptics`
 *
 * When the app gets wrapped in Capacitor for the native iOS / Android
 * builds, swap each method body to call `Haptics.impact({ style:
 * ImpactStyle.Light/Medium/Heavy })`. The call sites
 * (`haptics.light()`, etc.) don't change — that's the whole point of
 * the shim. Capacitor's haptics route through the platform's proper
 * haptic engine (Taptic on iOS, RichTapHaptics on supported Android),
 * which is noticeably more precise than `navigator.vibrate`.
 *
 * ## Feature detection
 *
 * `navigator.vibrate` is undefined in SSR contexts (no `navigator` global)
 * and in any browser without the Vibration API. The internal `pulse` helper
 * checks before calling and swallows it when unavailable — no errors, no
 * console noise, just a silent no-op.
 *
 * Note what that means and what it does not. A silent no-op is the right
 * behaviour for a keypad press, where the visual feedback carries the
 * message on its own. It is NOT sufficient for a cue that has no visual
 * counterpart: the end of a rest was relying on this buzz alone on a
 * platform where the buzz does not arrive, which is how a lifter ends up
 * waiting for a signal the app believes it sent.
 */

function pulse(durationMs: number): void {
  // SSR guard — `navigator` doesn't exist server-side, but this module
  // gets imported into client components that may serialise during
  // RSC payload generation. The optional chain handles that case
  // cleanly without a separate `typeof window !== "undefined"` branch.
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    // The Vibration API returns `false` if the request was suppressed
    // (e.g. by browser settings or user gesture requirements). We
    // don't act on the return — there's nothing useful to do if a
    // particular pulse silently fails, and logging it would spam.
    navigator.vibrate(durationMs);
  }
}

export const haptics = {
  /** Subtle 10ms pulse — digit keypress, backspace, clear-value. */
  light: (): void => pulse(10),
  /** Mid-weight 20ms pulse — field-advance, wheel-snap. */
  medium: (): void => pulse(20),
  /** Heavier 30ms pulse — set commit (final-field Done). */
  heavy: (): void => pulse(30),
} as const;
